import React from 'react';
import { Card } from 'react-bootstrap';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { format } from 'date-fns';
import { ReportSalesMonthlyResponseV4 } from '../../../../services/cm-group-report/response/ReportSalesMonthlyResponseV4';
import { formatPrice } from '../../../../shared/utils/formatPrice'; // Pastikan utilitas ini tersedia

interface ReportSalesMonthlyHighchartsV4Props {
    data: ReportSalesMonthlyResponseV4;
}

const ReportSalesMonthlyHighchartsV4: React.FC<ReportSalesMonthlyHighchartsV4Props> = ({ data }) => {

    // ================== TRANSFORMASI DATA ==================

    // Langkah 1: Pivot data untuk mengelompokkan berdasarkan bulan
    const pivotedData = data.data.reduce<Record<string, any>>((acc, item) => {
        const formattedMonth = format(new Date(item.month), 'MMM yyyy');
        if (!acc[formattedMonth]) {
            acc[formattedMonth] = { month: formattedMonth };
        }
        acc[formattedMonth][item.name] = Number(item.total_earning);
        return acc;
    }, {});

    // Langkah 2: Ubah kembali ke array dan urutkan berdasarkan tanggal
    const chartDataWide = Object.values(pivotedData).sort((a, b) => {
        return new Date(a.month).getTime() - new Date(b.month).getTime();
    });

    // Langkah 3: Dapatkan kategori untuk sumbu X
    const categories = chartDataWide.map(item => item.month);

    // Langkah 4: Buat format series untuk Highcharts
    const series: Highcharts.SeriesOptionsType[] = data.total_source.map(source => ({
        type: 'spline',
        name: source.name,
        color: source.color,
        data: chartDataWide.map(monthData => monthData[source.name] || 0),
        marker: {
            enabled: true,
            symbol: 'circle',
            radius: 3,
        },
    }));

    // ================== OPSI HIGHCHARTS ==================

    const getOptions = (): Highcharts.Options => ({
        chart: {
            type: 'spline',
            backgroundColor: 'transparent',
        },
        title: {
            text: 'Graph of Sales in the Last 12 Months',
        },
        xAxis: {
            categories: categories,
            crosshair: true,
        },
        yAxis: {
            title: {
                text: 'Total Sales Amount'
            },
            labels: {
                // Replikasi tickFormatter dari Recharts untuk format K/M
                formatter: function () {
                    const value = this.value as number;
                    if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
                    if (value >= 1e3) return `${(value / 1e3).toFixed(0)}K`;
                    return value.toString();
                },
            },
        },
        tooltip: {
            shared: true,
            // Menggunakan pointFormatter untuk memformat setiap nilai dengan formatPrice
            pointFormatter: function () {
                const value = this.y ?? 0;
                return `<span style="color:${this.color}">●</span> ${this.series.name}: <b>${formatPrice(value)}</b><br/>`;
            },
            headerFormat: '<span style="font-size: 10px"><b>{point.key}</b></span><br/>',
        },
        plotOptions: {
            spline: {
                lineWidth: 2,
                states: {
                    hover: {
                        lineWidth: 3,
                    },
                },
            },
        },
        series: series,
        credits: {
            enabled: false,
        },
    });

    return (
        <Card className="h-100 shadow">
            <Card.Body>
                <HighchartsReact
                    highcharts={Highcharts}
                    options={getOptions()}
                    containerProps={{ style: { height: '400px', width: '100%' } }}
                />
            </Card.Body>
        </Card>
    );
};

export default ReportSalesMonthlyHighchartsV4;